import { useCallback, useEffect, useRef } from 'react';
import type {
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  ReactNode,
} from 'react';
import { Menu } from 'lucide-react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { MainContent } from './MainContent';
import { AssistantDock } from '@/features/assistant/AssistantDock';
import { PortalAssistantProvider } from '@/features/assistant/PortalAssistantProvider';
import { useAssistantAccess } from '@/features/assistant/useAssistantAccess';
import { useNavigationStore } from '@/features/navigation/store';
import { config } from '@/config/env';
import { useNavigation } from '@/features/navigation/hooks';
import { serviceNavItemIds } from '@/features/navigation/config';
import { PersistentIframeRoutes } from '@/features/embedding/components/PersistentIframeRoutes';
import { useAuthorization } from '@/features/auth/hooks';
import { useShellI18n } from '@/features/language/useI18n';
import { findIframeServiceForShellPath } from '@/features/embedding/utils/routes';
import styles from './Shell.module.css';

const MOBILE_OVERVIEW_BUTTON_STORAGE_KEY = 'portal.mobileOverviewButtonPosition';
const MOBILE_OVERVIEW_BUTTON_MARGIN = 12;
const MOBILE_OVERVIEW_BUTTON_DRAG_THRESHOLD_PX = 6;
/** Matches the round button in Shell.module.css; used before layout is available. */
const MOBILE_OVERVIEW_BUTTON_FALLBACK_SIZE = {
  width: 48,
  height: 48,
};

const MOBILE_OVERVIEW_BUTTON_RESIZE_DEBOUNCE_MS = 150;

interface FloatingButtonPosition {
  x: number;
  y: number;
}

interface FloatingButtonSize {
  width: number;
  height: number;
}

/**
 * The stored anchor is a ratio of the usable travel, not an absolute pixel pair.
 * Absolute pixels have to be re-clamped whenever the viewport changes, and because
 * the clamped value replaces the original one, the button ratchets towards the top
 * left and never comes back. A ratio survives every viewport change untouched.
 */
interface FloatingButtonAnchor {
  xRatio: number;
  yRatio: number;
}

interface FloatingButtonDragState {
  pointerId: number;
  startClientX: number;
  startClientY: number;
  startX: number;
  startY: number;
  /** Measured once on pointer down so the move handler never reads back from layout. */
  size: FloatingButtonSize;
  hasDragged: boolean;
}

/** Top right: the only corner where the button does not sit on the page heading. */
const defaultMobileOverviewButtonAnchor: FloatingButtonAnchor = {
  xRatio: 1,
  yRatio: 0,
};

const isBrowser = () => typeof window !== 'undefined';

/**
 * The button is `position: fixed`, so its offsets resolve against the layout
 * viewport. `window.innerWidth/innerHeight` report the visual viewport, which
 * changes every time a mobile browser shows or hides its URL bar. Measuring that
 * instead is what made the button jump while scrolling.
 *
 * The fallback matters: jsdom has no layout engine and reports 0 here.
 */
const getViewportSize = (): FloatingButtonSize => ({
  width: document.documentElement.clientWidth || window.innerWidth,
  height: document.documentElement.clientHeight || window.innerHeight,
});

const measureMobileOverviewButtonSize = (button: HTMLButtonElement | null): FloatingButtonSize => ({
  width: button?.offsetWidth || MOBILE_OVERVIEW_BUTTON_FALLBACK_SIZE.width,
  height: button?.offsetHeight || MOBILE_OVERVIEW_BUTTON_FALLBACK_SIZE.height,
});

const getMobileOverviewButtonBounds = (size: FloatingButtonSize) => {
  const viewport = getViewportSize();

  return {
    minX: MOBILE_OVERVIEW_BUTTON_MARGIN,
    minY: MOBILE_OVERVIEW_BUTTON_MARGIN,
    maxX: Math.max(
      MOBILE_OVERVIEW_BUTTON_MARGIN,
      viewport.width - size.width - MOBILE_OVERVIEW_BUTTON_MARGIN
    ),
    maxY: Math.max(
      MOBILE_OVERVIEW_BUTTON_MARGIN,
      viewport.height - size.height - MOBILE_OVERVIEW_BUTTON_MARGIN
    ),
  };
};

const clampMobileOverviewButtonPosition = (
  position: FloatingButtonPosition,
  size: FloatingButtonSize
): FloatingButtonPosition => {
  if (!isBrowser()) {
    return position;
  }

  const { minX, minY, maxX, maxY } = getMobileOverviewButtonBounds(size);

  return {
    x: Math.min(Math.max(position.x, minX), maxX),
    y: Math.min(Math.max(position.y, minY), maxY),
  };
};

const toMobileOverviewButtonAnchor = (
  position: FloatingButtonPosition,
  size: FloatingButtonSize
): FloatingButtonAnchor => {
  const { minX, minY, maxX, maxY } = getMobileOverviewButtonBounds(size);
  const travelX = maxX - minX;
  const travelY = maxY - minY;

  return {
    xRatio: travelX > 0 ? (position.x - minX) / travelX : 0,
    yRatio: travelY > 0 ? (position.y - minY) / travelY : 0,
  };
};

const toMobileOverviewButtonPosition = (
  anchor: FloatingButtonAnchor,
  size: FloatingButtonSize
): FloatingButtonPosition => {
  const { minX, minY, maxX, maxY } = getMobileOverviewButtonBounds(size);

  return {
    x: minX + (maxX - minX) * anchor.xRatio,
    y: minY + (maxY - minY) * anchor.yRatio,
  };
};

const isValidAnchor = (value: unknown): value is FloatingButtonAnchor => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const anchor = value as Partial<FloatingButtonAnchor>;
  return Number.isFinite(anchor.xRatio) && Number.isFinite(anchor.yRatio);
};

const isValidPosition = (value: unknown): value is FloatingButtonPosition => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const position = value as Partial<FloatingButtonPosition>;
  return Number.isFinite(position.x) && Number.isFinite(position.y);
};

const getStoredMobileOverviewButtonAnchor = (size: FloatingButtonSize): FloatingButtonAnchor => {
  if (!isBrowser()) {
    return defaultMobileOverviewButtonAnchor;
  }

  try {
    const storedValue = window.localStorage.getItem(MOBILE_OVERVIEW_BUTTON_STORAGE_KEY);
    if (!storedValue) {
      return defaultMobileOverviewButtonAnchor;
    }

    const parsedValue = JSON.parse(storedValue) as unknown;

    if (isValidAnchor(parsedValue)) {
      return parsedValue;
    }

    // Absolute coordinates written by an earlier version.
    if (isValidPosition(parsedValue)) {
      return toMobileOverviewButtonAnchor(parsedValue, size);
    }

    return defaultMobileOverviewButtonAnchor;
  } catch {
    return defaultMobileOverviewButtonAnchor;
  }
};

const persistMobileOverviewButtonAnchor = (anchor: FloatingButtonAnchor) => {
  if (!isBrowser()) {
    return;
  }

  try {
    window.localStorage.setItem(MOBILE_OVERVIEW_BUTTON_STORAGE_KEY, JSON.stringify(anchor));
  } catch {
    // The button still works when persistent storage is unavailable.
  }
};

/**
 * Written straight to the DOM rather than through React state. A state update per
 * pointer move re-renders the whole shell, including the persistent iframes, which
 * is what made dragging feel sticky.
 */
const applyMobileOverviewButtonPosition = (
  button: HTMLButtonElement,
  position: FloatingButtonPosition
) => {
  button.style.transform = `translate3d(${Math.round(position.x)}px, ${Math.round(position.y)}px, 0)`;
};

/**
 * Shell layout configuration
 */
export interface ShellLayoutConfig {
  /** Whether to show the header */
  showHeader?: boolean;
  /** Whether to show the sidebar */
  showSidebar?: boolean;
  /** Whether to show the footer */
  showFooter?: boolean;
}

/**
 * Shell component props
 */
export interface ShellProps {
  /** Content to display within the shell */
  children?: ReactNode;
  /** Layout configuration */
  layoutConfig?: ShellLayoutConfig;
}

const getEmbeddedNavigationServiceId = (state: unknown) => {
  if (!state || typeof state !== 'object') {
    return null;
  }

  const value = (state as { portalEmbeddedNavigationServiceId?: unknown })
    .portalEmbeddedNavigationServiceId;
  return typeof value === 'string' ? value : null;
};

/**
 * Main Shell component that provides the application structure
 *
 * Features:
 * - Responsive layout (Sidebar + Header + Content)
 * - State management integration
 * - Configurable layout options
 */
export function Shell(props: ShellProps) {
  return (
    <PortalAssistantProvider>
      <ShellLayout {...props} />
    </PortalAssistantProvider>
  );
}

function ShellLayout({ children, layoutConfig = {} }: ShellProps) {
  const { showSidebar = true } = layoutConfig;
  const { mobileMenuOpen, setMobileMenuOpen, assistantOpen: storedAssistantOpen } = useNavigation();
  const { canViewAssistant } = useAssistantAccess();
  const assistantOpen = canViewAssistant && storedAssistantOpen;
  useEffect(() => {
    if (!canViewAssistant) useNavigationStore.getState().closeAssistant();
  }, [canViewAssistant]);
  const { filterServices } = useAuthorization();
  const { t } = useShellI18n();
  const location = useLocation();
  const navigationType = useNavigationType();
  const mobileOverviewButtonRef = useRef<HTMLButtonElement | null>(null);
  const mobileOverviewButtonDragRef = useRef<FloatingButtonDragState | null>(null);
  const suppressMobileOverviewButtonClickRef = useRef(false);
  const mobileOverviewButtonAnchorRef = useRef<FloatingButtonAnchor | null>(null);
  const mobileOverviewButtonPositionRef = useRef<FloatingButtonPosition>({ x: 0, y: 0 });
  const content = children ?? <Outlet />;
  const embeddedServices = filterServices(config().EMBEDDED_SERVICES || []);
  const activeIframeService = findIframeServiceForShellPath(embeddedServices, location.pathname);
  const hasActivePersistentIframeRoute = Boolean(activeIframeService);
  const isKnownServiceRoute = serviceNavItemIds.some(
    (serviceId) =>
      location.pathname === `/${serviceId}` || location.pathname.startsWith(`/${serviceId}/`)
  );
  const isEmbedRoute =
    hasActivePersistentIframeRoute || isKnownServiceRoute || location.pathname.startsWith('/apps/');
  const embeddedNavigationServiceId = getEmbeddedNavigationServiceId(location.state);

  /**
   * A ref callback rather than an effect, because the button is rendered
   * conditionally and remounts whenever the mobile menu opens or closes. This runs
   * during commit, before paint, so the button never flashes in the corner first.
   */
  const attachMobileOverviewButton = useCallback((button: HTMLButtonElement | null) => {
    mobileOverviewButtonRef.current = button;

    if (!button || !isBrowser()) {
      return;
    }

    const size = measureMobileOverviewButtonSize(button);

    if (mobileOverviewButtonAnchorRef.current === null) {
      mobileOverviewButtonAnchorRef.current = getStoredMobileOverviewButtonAnchor(size);
    }

    const position = toMobileOverviewButtonPosition(mobileOverviewButtonAnchorRef.current, size);
    mobileOverviewButtonPositionRef.current = position;
    applyMobileOverviewButtonPosition(button, position);
  }, []);

  useEffect(() => {
    if (!isBrowser()) {
      return undefined;
    }

    let timeoutId: number | undefined;

    /*
     * Projecting the stored ratio onto the new viewport is idempotent, so repeated
     * resizes cannot erode the position the way re-clamping absolute pixels did.
     */
    const reposition = () => {
      const button = mobileOverviewButtonRef.current;
      const anchor = mobileOverviewButtonAnchorRef.current;

      if (!button || !anchor || mobileOverviewButtonDragRef.current) {
        return;
      }

      const position = toMobileOverviewButtonPosition(
        anchor,
        measureMobileOverviewButtonSize(button)
      );
      mobileOverviewButtonPositionRef.current = position;
      applyMobileOverviewButtonPosition(button, position);
    };

    const handleResize = () => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(reposition, MOBILE_OVERVIEW_BUTTON_RESIZE_DEBOUNCE_MS);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  const handleMobileOverviewButtonPointerDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }

    const size = measureMobileOverviewButtonSize(event.currentTarget);
    const constrainedPosition = clampMobileOverviewButtonPosition(
      mobileOverviewButtonPositionRef.current,
      size
    );

    mobileOverviewButtonDragRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startX: constrainedPosition.x,
      startY: constrainedPosition.y,
      size,
      hasDragged: false,
    };

    mobileOverviewButtonPositionRef.current = constrainedPosition;
    applyMobileOverviewButtonPosition(event.currentTarget, constrainedPosition);

    if (typeof event.currentTarget.setPointerCapture === 'function') {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
  };

  const handleMobileOverviewButtonPointerMove = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const dragState = mobileOverviewButtonDragRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    const deltaX = event.clientX - dragState.startClientX;
    const deltaY = event.clientY - dragState.startClientY;
    const distance = Math.hypot(deltaX, deltaY);

    if (!dragState.hasDragged && distance < MOBILE_OVERVIEW_BUTTON_DRAG_THRESHOLD_PX) {
      return;
    }

    if (!dragState.hasDragged) {
      dragState.hasDragged = true;
      event.currentTarget.setAttribute('data-dragging', 'true');
    }

    const position = clampMobileOverviewButtonPosition(
      {
        x: dragState.startX + deltaX,
        y: dragState.startY + deltaY,
      },
      dragState.size
    );

    mobileOverviewButtonPositionRef.current = position;
    applyMobileOverviewButtonPosition(event.currentTarget, position);
    event.preventDefault();
  };

  const finishMobileOverviewButtonDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const dragState = mobileOverviewButtonDragRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    if (
      typeof event.currentTarget.hasPointerCapture === 'function' &&
      event.currentTarget.hasPointerCapture(event.pointerId) &&
      typeof event.currentTarget.releasePointerCapture === 'function'
    ) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    suppressMobileOverviewButtonClickRef.current = dragState.hasDragged;
    mobileOverviewButtonDragRef.current = null;
    event.currentTarget.removeAttribute('data-dragging');

    if (dragState.hasDragged) {
      const anchor = toMobileOverviewButtonAnchor(
        mobileOverviewButtonPositionRef.current,
        dragState.size
      );
      mobileOverviewButtonAnchorRef.current = anchor;
      persistMobileOverviewButtonAnchor(anchor);
    }
  };

  const handleMobileOverviewButtonClick = (event: ReactMouseEvent<HTMLButtonElement>) => {
    if (suppressMobileOverviewButtonClickRef.current) {
      event.preventDefault();
      suppressMobileOverviewButtonClickRef.current = false;
      return;
    }

    // Ein Hamburger-Zeichen verspricht das Menue. Vorher navigierte der Knopf
    // zur Uebersicht, was mit dem Zeichen und dem Titel nichts zu tun hatte.
    setMobileMenuOpen(true);
  };

  return (
    <div className={styles.shell}>
      <div className={styles.ambientA} aria-hidden="true" />
      <div className={styles.ambientB} aria-hidden="true" />

      {/*
        The scrim is a sibling of the drawer, never a child of it. `.sidebarWrapper`
        carries a `transform` on mobile, and a transformed element becomes the containing
        block for its `position: fixed` descendants. Nested, the scrim's `inset: 0`
        resolved against the drawer's own box: it dimmed nothing, sat directly behind the
        translucent sidebar (which turned grey in the light theme) and swallowed no taps
        outside it.
      */}
      {showSidebar && mobileMenuOpen && (
        <div
          className={styles.overlay}
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {showSidebar && (
        <div className={`${styles.sidebarWrapper} ${mobileMenuOpen ? styles.mobileOpen : ''}`}>
          <Sidebar />
        </div>
      )}

      {showSidebar && canViewAssistant && <AssistantDock />}

      {/*
        Position and drag state live outside React on purpose, as a `transform` and a
        `data-dragging` attribute. React only touches style properties and attributes
        it set itself, so those writes survive every re-render. Do not add a `style`
        prop or a dynamic `className` here — either one would hand ownership back to
        React and overwrite the button's position mid-drag.
      */}
      {showSidebar && !mobileMenuOpen && !assistantOpen && (
        <button
          ref={attachMobileOverviewButton}
          id="mobile-menu-button"
          type="button"
          className={styles.mobileMenuButton}
          onPointerDown={handleMobileOverviewButtonPointerDown}
          onPointerMove={handleMobileOverviewButtonPointerMove}
          onPointerUp={finishMobileOverviewButtonDrag}
          onPointerCancel={finishMobileOverviewButtonDrag}
          onClick={handleMobileOverviewButtonClick}
          aria-label={t('Open navigation menu')}
          title={t('Menu')}
        >
          <Menu aria-hidden="true" />
        </button>
      )}

      <div className={styles.mainWrapper}>
        <MainContent fullWidth={isEmbedRoute} noPadding={isEmbedRoute}>
          <PersistentIframeRoutes
            activePath={location.pathname}
            activeSearch={location.search}
            activeHash={location.hash}
            embeddedNavigationServiceId={embeddedNavigationServiceId}
            navigationType={navigationType}
          />
          {hasActivePersistentIframeRoute ? null : content}
        </MainContent>
      </div>
    </div>
  );
}

export default Shell;
